/**
 * Ce que le fournisseur a répondu (V1.6-14.6).
 *
 * Ce que cet écran tient, et qui est tout le ticket :
 *
 * * **trois questions, trois cartes.** « Statut commande » dit où en est
 *   l'engagement, « Envoi fournisseur » ce qui est sorti, et celle-ci ce que le
 *   fournisseur en a dit. Les fondre ferait croire qu'un envoi réussi vaut un
 *   accord, ou qu'un refus annule la commande ;
 * * **on n'affiche jamais « commande modifiée ».** Le bon n'a pas été modifié.
 *   Une date proposée est montrée comme une proposition, à côté, jamais à la
 *   place de la date attendue ;
 * * **l'historique reste lisible en entier.** Un fournisseur qui dit non puis
 *   oui a dit les deux, et la première réponse explique pourquoi quelqu'un a
 *   relancé ;
 * * **l'écran ne décide d'aucun droit.** `can_record` vient du serveur.
 *
 * Aucune réponse existante ne se modifie : l'histoire est append-only côté
 * base, et offrir un bouton « corriger » ici mentirait sur ce qui est possible.
 */
import { useState } from "react";
import { CheckCircle2, MessageSquare, XCircle } from "lucide-react";

import {
  usePurchaseOrderAcknowledgements,
  usePurchaseOrderDispatch,
  useRecordSupplierAcknowledgement,
} from "@/api/purchase-orders";
import type {
  AcknowledgementChannel,
  AcknowledgementHistory,
  PlannedPurchaseOrderDetail,
  SupplierAcknowledgement,
  SupplierAcknowledgementStatus,
} from "@/api/types";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import {
  formatAcknowledgementChannel,
  formatAcknowledgementState,
  formatAcknowledgementStatus,
  formatDate,
  formatDateTime,
} from "@/lib/format";
import { reportMutationError } from "@/lib/mutation-errors";
import { acknowledgementTone } from "@/lib/stock";

export function AcknowledgementCard({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  const { data, isLoading } = usePurchaseOrderAcknowledgements(detail.order.id);

  return (
    <Card>
      <CardHeader title="Confirmation fournisseur" />
      <CardBody className="space-y-3">
        {isLoading || !data ? (
          <p className="text-[13px] text-ink-subtle">Lecture des réponses…</p>
        ) : (
          <AcknowledgementBody detail={detail} history={data} />
        )}
      </CardBody>
    </Card>
  );
}

function AcknowledgementBody({
  detail,
  history,
}: {
  detail: PlannedPurchaseOrderDetail;
  history: AcknowledgementHistory;
}) {
  const latest = history.items.at(-1);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge tone={acknowledgementTone(history.state)}>
          {formatAcknowledgementState(history.state)}
        </StatusBadge>
      </div>

      {history.state === "NOT_ACKNOWLEDGED" ? (
        <p className="text-[13px] text-ink-subtle">
          Aucune réponse enregistrée. Un fournisseur n'est pas tenu de répondre : la commande
          reste passée et la marchandise peut arriver sans confirmation.
        </p>
      ) : null}

      {latest ? <Latest entry={latest} /> : null}

      {history.items.length > 1 ? <Timeline items={history.items} /> : null}

      {history.can_record ? <Recorder detail={detail} /> : null}
    </>
  );
}

/** La dernière réponse, en détail. C'est elle qui fait l'état courant. */
function Latest({ entry }: { entry: SupplierAcknowledgement }) {
  return (
    <dl className="grid gap-x-4 gap-y-1 text-[13px] sm:grid-cols-[auto_1fr]">
      <Row label="Reçue le" value={formatDateTime(entry.received_at)} />
      <Row label="Canal" value={formatAcknowledgementChannel(entry.channel)} />
      {entry.responder_name ? <Row label="Contact" value={entry.responder_name} /> : null}
      {entry.external_reference ? (
        <Row label="Leur référence" value={entry.external_reference} />
      ) : null}
      {entry.proposed_expected_on ? (
        <Row
          label="Date proposée"
          value={`${formatDate(entry.proposed_expected_on)} — proposée, pas appliquée`}
        />
      ) : null}
      {entry.note ? <Row label="Note" value={entry.note} /> : null}
      <Row label="Saisie par" value={`${entry.recorded_by}, ${formatDateTime(entry.created_at)}`} />
    </dl>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-ink-subtle">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </>
  );
}

/** L'historique compact. Les réponses précédentes expliquent la dernière. */
function Timeline({ items }: { items: SupplierAcknowledgement[] }) {
  return (
    <div className="space-y-1 border-t border-line pt-2">
      <p className="text-[12px] font-medium text-ink-subtle">Réponses précédentes</p>
      <ul className="space-y-1 text-[13px]">
        {items.slice(0, -1).map((entry) => (
          <li key={entry.id} className="text-ink-subtle">
            {formatAcknowledgementStatus(entry.status)} — {formatDateTime(entry.received_at)} (
            {formatAcknowledgementChannel(entry.channel)})
          </li>
        ))}
      </ul>
    </div>
  );
}

const CHANNELS: { value: AcknowledgementChannel; label: string }[] = [
  { value: "EMAIL", label: "Courriel" },
  { value: "PHONE", label: "Téléphone" },
  { value: "PORTAL", label: "Portail fournisseur" },
  { value: "IN_PERSON", label: "En personne" },
  { value: "OTHER", label: "Autre" },
];

const CHOICES: { value: SupplierAcknowledgementStatus; label: string; icon: typeof CheckCircle2 }[] =
  [
    { value: "CONFIRMED", label: "Confirmé", icon: CheckCircle2 },
    { value: "DECLINED", label: "Refusé", icon: XCircle },
    { value: "CHANGES_REQUESTED", label: "Changements demandés", icon: MessageSquare },
  ];

function Recorder({ detail }: { detail: PlannedPurchaseOrderDetail }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<SupplierAcknowledgementStatus>("CONFIRMED");
  const [channel, setChannel] = useState<AcknowledgementChannel>("EMAIL");
  const [responder, setResponder] = useState("");
  const [note, setNote] = useState("");
  const record = useRecordSupplierAcknowledgement();
  const toast = useToast();

  // Le document auquel la réponse se rattache : celui que le serveur dit avoir
  // envoyé. La requête est celle de la carte d'envoi, dédupliquée par sa clé,
  // donc elle ne coûte pas un second appel.
  //
  // Sans message parti, il n'y a rien à accuser : le bouton n'existe pas, et le
  // serveur refuserait de toute façon — la barrière est là, pas ici.
  const { data: sent } = usePurchaseOrderDispatch(detail.order.id);
  const messageId = sent?.dispatch_state === "SENT" || sent?.dispatch_state === "UNKNOWN_DELIVERY"
    ? sent.outbound_message_id
    : null;

  // « Changements demandés » sans rien dire n'apprend rien à personne, et le
  // serveur le refuse. On le dit ici plutôt que de laisser cliquer dans le vide.
  const incomplete = status === "CHANGES_REQUESTED" && note.trim() === "";

  async function submit() {
    if (!messageId) return;
    try {
      await record.mutateAsync({
        orderId: detail.order.id,
        body: {
          outbound_message_id: messageId,
          status,
          channel,
          received_at: new Date().toISOString(),
          // Tirée à chaque ouverture du dialogue : deux réponses réellement
          // différentes doivent porter deux clés, et un double-clic la même.
          client_operation_id: crypto.randomUUID(),
          responder_name: responder.trim() || null,
          note: note.trim() || null,
        },
      });
      toast.success(
        "Réponse enregistrée",
        "Le bon de commande n'a pas été modifié.",
      );
      setOpen(false);
      setResponder("");
      setNote("");
    } catch (error) {
      reportMutationError(toast, "Réponse non enregistrée", error);
    }
  }

  if (!messageId) return null;

  return (
    <div className="border-t border-line pt-3">
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Enregistrer une réponse fournisseur
      </Button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Réponse du fournisseur"
        description="Ce que le fournisseur a dit est enregistré tel quel. Le bon de commande n'est pas modifié."
      >
        <div className="space-y-3">
          <fieldset className="space-y-1">
            <legend className="text-[13px] font-medium text-ink">Réponse</legend>
            {CHOICES.map((choice) => (
              <label key={choice.value} className="flex items-center gap-2 text-[13px]">
                <input
                  type="radio"
                  name="ack-status"
                  value={choice.value}
                  checked={status === choice.value}
                  onChange={() => setStatus(choice.value)}
                />
                <choice.icon size={14} aria-hidden />
                {choice.label}
              </label>
            ))}
          </fieldset>

          <label className="block space-y-1 text-[13px]">
            <span className="font-medium text-ink">Canal</span>
            <select
              className="w-full rounded border border-line px-2 py-1"
              value={channel}
              onChange={(event) => setChannel(event.target.value as AcknowledgementChannel)}
            >
              {CHANNELS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1 text-[13px]">
            <span className="font-medium text-ink">Contact chez le fournisseur</span>
            <input
              className="w-full rounded border border-line px-2 py-1"
              value={responder}
              onChange={(event) => setResponder(event.target.value)}
              placeholder="Facultatif"
            />
          </label>

          <label className="block space-y-1 text-[13px]">
            <span className="font-medium text-ink">
              Note {status === "CHANGES_REQUESTED" ? "(obligatoire)" : "(facultative)"}
            </span>
            <textarea
              className="w-full rounded border border-line px-2 py-1"
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Ce que le fournisseur a dit, dans ses mots."
            />
          </label>

          {incomplete ? (
            <p className="text-[13px] text-ink-subtle">
              Dire que le fournisseur propose autre chose sans dire quoi n'apprend rien à
              personne.
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button onClick={submit} disabled={incomplete || record.isPending}>
              Enregistrer
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
